import * as types from "../types/actionTypes";
import objectAssign from "object-assign";
import fetch from "node-fetch";
import utils from "./../../universal/utils";
import * as cookieStorage from "./common/cookieStorage";

const cStorage = cookieStorage.getCookieStorage();
const basePath = utils.getEdgeBasePath();

function fetchAdmin(url, options = {}) {
  return fetch(url, {
    ...options,
    credentials: "include",
  });
}

async function parseAdminResponse(apiResult) {
  let result = {};

  try {
    result = await apiResult.json();
  } catch (err) {
    if (!apiResult.ok) {
      throw new Error(`Request failed (${apiResult.status})`);
    }

    throw err;
  }

  if (!apiResult.ok || result?.ok === false) {
    throw new Error(
      result?.error ||
        result?.err ||
        result?.message ||
        `Request failed (${apiResult.status})`
    );
  }

  return result;
}

export function setUser({ user }) {
  return {
    type: types.LOAD_USER_PROFILE,
    user,
  };
}

export function updateAccountFormField({ name, value }) {
  return {
    type: types.ACCOUNT_FORM_FIELD_CHANGE,
    name,
    value,
  };
}

export function dismissSuccessAccountAlert() {
  return {
    type: types.ACCOUNT_SUCCESS_ALERT_SNOOZE,
  };
}

export function setAccountUpdateAlert(text) {
  return {
    type: types.ACCOUNT_UPDATE_ALERT,
    text,
  };
}

export function dismissAccountUpdateAlert() {
  return {
    type: types.ACCOUNT_UPDATE_ALERT_SNOOZE,
  };
}

export function logout() {
  return {
    type: types.PERFORM_LOGOUT,
  };
}

export function collectMetric(metadata) {
  const stats = cStorage.getObject("stats");
  const { visitorId } = stats;

  return function (dispatch) {
    fetch(`${basePath}/collector/collect/${visitorId}`, {
      method: "POST",
      body: JSON.stringify(metadata),
      headers: { "Content-Type": "application/json" },
    }).then(function (apiResult) {
      return apiResult.json().then(function (json) {
        if (json.ok) {
          console.log("metric reported correctly");
        }
      });
    });
  };
}

export function getTrackingVisitorId() {
  const statsFromCookieStorage = cStorage.getObject("stats");

  if (statsFromCookieStorage !== null) {
    // User has an active visitorId, don't ask for one.
    return function (dispatch) {
      dispatch({
        type: types.STATS_UPDATED,
        stats: statsFromCookieStorage,
      });
    };
  }

  return function (dispatch) {
    fetch(`${basePath}/collector/visitor/id`).then(function (apiResult) {
      return apiResult.json().then(function (result) {
        const stats = {
          data: {
            likes: {
              products: [],
              shops: [],
            },
          },
          visitorId: result.visitorId,
        };

        dispatch({
          type: types.STATS_UPDATED,
          stats,
        });

        cStorage.setObject("stats", stats);
      });
    });
  };
}

export function updateUserELO({ elo, userId, user }) {
  return function (dispatch) {
    fetchAdmin(`${basePath}/api/v7/admin/user/update/adpass/${user.userId}`, {
      method: "POST",
      body: JSON.stringify({ elo, userId }),
      headers: { "Content-Type": "application/json" },
    }).then(function (apiResult) {
      return apiResult.json().then(function (result) {
        dispatch({
          type: types.ADMIN_SET_ELO_SUCCESS,
          result,
        });

        getAllUsers({ userId: user.userId })(dispatch);
      });
    });
  };
}

export function updateSetUserAsRejected({ userId, user, material }) {
  return function (dispatch) {
    fetchAdmin(`${basePath}/api/v7/admin/user/setasrejected/adpass/${user.userId}`, {
      method: "POST",
      body: JSON.stringify({ userId, material }),
      headers: { "Content-Type": "application/json" },
    }).then(function (apiResult) {
      return apiResult.json().then(function (result) {
        dispatch({
          type: types.ADMIN_SET_PLAYER_AS_REJECTED_SUCCESS,
          result,
        });

        getAllUsers({ userId: user.userId })(dispatch);
      });
    });
  };
}

export function updateSetUserAsVerified({
  userId,
  user,
  bypassPhoneVerification = false,
  overrideNotEligible = false,
  overrideReason = "",
}) {
  return async function (dispatch) {
    try {
      const apiResult = await fetchAdmin(
        `${basePath}/api/v7/admin/user/setasverified/adpass/${user.userId}`,
        {
          method: "POST",
          body: JSON.stringify({
            userId,
            bypassPhoneVerification,
            overrideNotEligible,
            overrideReason,
          }),
          headers: { "Content-Type": "application/json" },
        }
      );
      const result = await parseAdminResponse(apiResult);
      const refreshResult = await getAllUsers({ userId: user.userId })(dispatch);
      if (refreshResult?.ok === false) {
        return {
          ok: true,
          refreshError: refreshResult.error || "Failed to refresh admin users",
        };
      }

      dispatch({
        type: types.ADMIN_SET_PLAYER_AS_VERIFIED_SUCCESS,
        result,
      });

      return { ok: true };
    } catch (err) {
      console.error("Failed to set user as verified:", err);
      return { ok: false, error: err.message || "Request failed" };
    }
  };
}

export function initiateUserPhoneVerification({ userId, user }) {
  return async function (dispatch) {
    try {
      const apiResult = await fetchAdmin(
        `${basePath}/api/v7/admin/user/initiate-phone-verification/adpass/${user.userId}`,
        {
          method: "POST",
          body: JSON.stringify({ userId }),
          headers: { "Content-Type": "application/json" },
        }
      );
      await parseAdminResponse(apiResult);
      const refreshResult = await getAllUsers({ userId: user.userId })(dispatch);
      if (refreshResult?.ok === false) {
        return {
          ok: true,
          refreshError: refreshResult.error || "Failed to refresh admin users",
        };
      }

      return { ok: true };
    } catch (err) {
      console.error("Failed to initiate phone verification:", err);
      return { ok: false, error: err.message || "Request failed" };
    }
  };
}

export function updateUserAsBanned({ days, reason, userId, user }) {
  return function (dispatch) {
    fetchAdmin(`${basePath}/api/v7/admin/user/setasbanned/adpass/${user.userId}`, {
      method: "POST",
      body: JSON.stringify({ userId, days, reason }),
      headers: { "Content-Type": "application/json" },
    }).then(function (apiResult) {
      return apiResult.json().then(function (result) {
        dispatch({
          type: types.ADMIN_SET_PLAYER_BANNED_SUCCESS,
          result,
        });

        getAllUsers({ userId: user.userId })(dispatch);
      });
    });
  };
}

export function updateSetUserAsNotEligible({ userId, reason, user }) {
  return function (dispatch) {
    fetchAdmin(
      `${basePath}/api/v7/admin/user/setasnoteligible/adpass/${user.userId}`,
      {
        method: "POST",
        body: JSON.stringify({ userId, reason }),
        headers: { "Content-Type": "application/json" },
      }
    ).then(function (apiResult) {
      return apiResult.json().then(function (result) {
        dispatch({
          type: types.ADMIN_SET_PLAYER_AS_NOT_ELIGIBLE_SUCCESS,
          result,
        });

        getAllUsers({ userId: user.userId })(dispatch);
      });
    });
  };
}

export function updateUserValveAndFaceitStats({ stats, userId, user }) {
  return function (dispatch) {
    fetchAdmin(
      `${basePath}/api/v7/admin/user/setplayerstatsforfaceitvalve/adpass/${user.userId}`,
      {
        method: "POST",
        body: JSON.stringify({ userId, stats }),
        headers: { "Content-Type": "application/json" },
      }
    ).then(function (apiResult) {
      return apiResult.json().then(function (result) {
        dispatch({
          type: types.ADMIN_SET_PLAYER_STATS_SUCCESS,
          result,
        });

        getAllUsers({ userId: user.userId })(dispatch);
      });
    });
  };
}

export function setPlayerTrackerStatsDialogState(aBool) {
  return {
    type: types.ADMIN_SET_PLAYER_TRACKER_STATS_DIALOG_STATE,
    state: aBool, // true = open / false = close
  };
}

export function setPlayerBannedDialogState(aBool) {
  return {
    type: types.ADMIN_SET_PLAYER_BANNED_DIALOG_STATE,
    state: aBool, // true = open / false = close
  };
}

export function setPlayerRejectedDialogState(aBool) {
  return {
    type: types.ADMIN_SET_PLAYER_REJECTED_DIALOG_STATE,
    state: aBool, // true = open / false = close
  };
}

export function setPlayerNotEligibleDialogState(aBool) {
  return {
    type: types.ADMIN_SET_PLAYER_NOT_ELIGIBLE_DIALOG_STATE,
    state: aBool, // true = open / false = close
  };
}

export function setPlayerVerifiedDialogState(aBool) {
  return {
    type: types.ADMIN_SET_PLAYER_VERIFIED_DIALOG_STATE,
    state: aBool, // true = open / false = close
  };
}

export function setELODialogState(aBool) {
  return {
    type: types.ADMIN_SET_ELO_DIALOG_STATE,
    state: aBool, // true = open / false = close
  };
}

export function setAdminTargetUserContext({ userId, row, material }) {
  return {
    type: types.ADMIN_SET_TARGET_USER_CONTEXT,
    userId,
    row,
    ...(material !== undefined ? { material } : {}),
  };
}

export function getAllUsers({ userId }) {
  if (userId) {
    return async function (dispatch) {
      try {
        const apiResult = await fetchAdmin(
          `${basePath}/api/v7/admin/users/adpass/${userId}`
        );
        const result = await parseAdminResponse(apiResult);

        dispatch({
          type: types.ADMIN_LOAD_USERS,
          users: Array.isArray(result.users) ? result.users : [],
        });
        return {
          ok: true,
          users: Array.isArray(result.users) ? result.users : [],
        };
      } catch (err) {
        console.error("Failed to load admin users:", err);
        return {
          ok: false,
          error: err.message || "Failed to load admin users",
        };
      }
    };
  }
}

export function submitAccountChanges(data) {
  return function (dispatch) {
    dispatch({
      type: types.ACCOUNT_UPDATE_DURING,
    });

    fetch(`${basePath}/api/v1/account`, {
      body: JSON.stringify({
        data,
      }),
      cache: "no-cache",
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      method: "POST",
      mode: "cors",
      redirect: "follow",
      referrer: "no-referrer",
    }).then(function (apiResult) {
      return apiResult.json().then(function (response) {
        return dispatch({
          type: types.ACCOUNT_UPDATE_COMPLETE,
          response,
        });
      });
    });
  };
}

export function getAllReportedPlayers({ userId }) {
  if (userId) {
    return async function (dispatch) {
      try {
        const apiResult = await fetchAdmin(
          `${basePath}/api/v7/admin/reported-players/adpass/${userId}`
        );
        const result = await parseAdminResponse(apiResult);

        dispatch({
          type: types.ADMIN_LOAD_REPORTED_PLAYERS,
          reportedPlayers: Array.isArray(result.reportedPlayers)
            ? result.reportedPlayers
            : [],
        });
      } catch (err) {
        console.error("Failed to load reported players:", err);
      }
    };
  }
}

export function setOnlineUserCount({ count }) {
  return {
    type: types.SET_ONLINE_USER_COUNT,
    count,
  };
}

export function getAdminQueueAnalitycsSummary({ user }) {
  return function (dispatch) {
    if (!user || !user.userId) return;
    fetchAdmin(
      `${basePath}/api/v7/admin/queue-analitycs-summary/adpass/${user.userId}`
    )
      .then(function (apiResult) {
        return apiResult.json().then(function (result) {
          if (result && result.ok) {
            dispatch({
              type: types.SET_QUEUE_ANALITYCS_SUMMARY,
              queueAnalitycsSummary: {
                onlineUserCount: result.onlineUserCount || 0,
                totalPlayersInQueue: result.totalPlayersInQueue || 0,
                totalLobbies: result.totalLobbies || 0,
                averageQueueDuration: result.averageQueueDuration || 0,
              },
            });
          }
        });
      })
      .catch(function (err) {
        console.error("Failed to load admin queue analitycs summary:", err);
      });
  };
}

export function getAdminMMActivitySummary({ user }) {
  return function (dispatch) {
    if (!user || !user.userId) return;
    fetchAdmin(`${basePath}/api/v7/admin/mm-activity-summary/adpass/${user.userId}`)
      .then(function (apiResult) {
        return apiResult.json().then(function (result) {
          if (result && result.ok) {
            dispatch({
              type: types.SET_MM_ACTIVITY_SUMMARY,
              mmActivitySummary: result.mmActivitySummary,
            });
          }
        });
      });
  }
}

export function getAdminNewestPlayers({ user }) {
  return function (dispatch) {
    if (!user || !user.userId) return;
    fetchAdmin(`${basePath}/api/v7/admin/newest-players/adpass/${user.userId}`)
      .then(function (apiResult) {
        return apiResult.json().then(function (result) {
          if (result && result.ok) {
            dispatch({
              type: types.SET_NEWEST_PLAYERS,
              newestPlayers: result.newestPlayers,
            });
          }
        });
      });
  }
}

export function getAdminDaysDataForMatchesChart({ user, days }) {
  return function (dispatch) {
    if (!user || !user.userId || !days) return;

    const daysMap = {
      7: {
        actionType: types.SET_SEVEN_DAYS_MATCHMAKING_DATA_FOR_CHART,
        resultKey: "sevenDaysMatchmakingDataForChart",
      },
      30: {
        actionType: types.SET_THIRTY_DAYS_MATCHMAKING_DATA_FOR_CHART,
        resultKey: "thirtyDaysMatchmakingDataForChart",
      },
      90: {
        actionType: types.SET_NINETY_DAYS_MATCHMAKING_DATA_FOR_CHART,
        resultKey: "ninetyDaysMatchmakingDataForChart",
      },
    };

    const config = daysMap[days];
    if (!config) return;

    fetchAdmin(
      `${basePath}/api/v7/admin/data-for-matches-chart/${days}/adpass/${user.userId}`
    )
      .then((apiResult) => apiResult.json())
      .then((result) => {
        if (result && result.ok) {
          dispatch({
            type: config.actionType,
            [config.resultKey]: result[config.resultKey],
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching chart data:", err);
      });
  };
}

export function getAdminDataForSignupsChart({ user, period }) {
  return function (dispatch) {
    if (!user || !user.userId || !period) return;

    const periodMap = {
      week: {
        actionType: types.SET_WEEKLY_DATA_FOR_CHART,
        resultKey: "weeklyDataForChart",
      },
      month: {
        actionType: types.SET_MONTHLY_DATA_FOR_CHART,
        resultKey: "monthlyDataForChart",
      },
      year: {
        actionType: types.SET_YEARLY_DATA_FOR_CHART,
        resultKey: "yearlyDataForChart",
      },
    };

    const config = periodMap[period];
    if (!config) return;

    fetchAdmin(
      `${basePath}/api/v7/admin/data-for-signups-chart/${period}/adpass/${user.userId}`
    )
      .then((apiResult) => apiResult.json())
      .then((result) => {
        if (result && result.ok) {
          dispatch({
            type: config.actionType,
            [config.resultKey]: result[config.resultKey],
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching signups chart data:", err);
      });
  };
}

export function getMatchDurationDistribution({ user }) {
  return function (dispatch) {
    if (!user || !user.userId) return;
    fetchAdmin(
      `${basePath}/api/v7/admin/match-duration-distribution/adpass/${user.userId}`
    )
      .then((apiResult) => apiResult.json())
      .then((result) => {
        if (result && result.ok) {
          dispatch({
            type: types.SET_MATCH_DURATION_DATA,
            matchDurationData: result.matchDurationData,
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching match duration distribution:", err);
      });
  };
}

export function getAdminDaysDataForQueueChart({ user, days }) {
  return function (dispatch) {
    if (!user || !user.userId || !days) return;

    const daysMap = {
      7: {
        actionType: types.SET_SEVEN_DAYS_QUEUE_DATA_FOR_CHART,
        resultKey: "sevenDaysQueueDataForChart",
      },
      30: {
        actionType: types.SET_THIRTY_DAYS_QUEUE_DATA_FOR_CHART,
        resultKey: "thirtyDaysQueueDataForChart",
      },
      90: {
        actionType: types.SET_NINETY_DAYS_QUEUE_DATA_FOR_CHART,
        resultKey: "ninetyDaysQueueDataForChart",
      },
    };

    const config = daysMap[days];
    if (!config) return;

    fetchAdmin(
      `${basePath}/api/v7/admin/data-for-queue-chart/${days}/adpass/${user.userId}`
    )
      .then((apiResult) => apiResult.json())
      .then((result) => {
        if (result && result.ok) {
          dispatch({
            type: config.actionType,
            [config.resultKey]: result[config.resultKey],
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching queue chart data:", err);
      });
  };
}

export function getAdminPlayersQueuedPerDayData({ user }) {
  return function (dispatch) {
    if (!user || !user.userId) return;

    fetchAdmin(
      `${basePath}/api/v7/admin/players-queued-per-day/adpass/${user.userId}`
    )
      .then((apiResult) => apiResult.json())
      .then((result) => {
        if (result && result.ok) {
          dispatch({
            type: types.SET_PLAYERS_QUEUED_PER_DAY_DATA,
            playersQueuedPerDayData: result.playersQueuedPerDayData,
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching players queued per day data:", err);
      });
  };
}

export function getAdminQueueEfficiencyPerDayData({ user }) {
  return function (dispatch) {
    if (!user || !user.userId) return;

    fetchAdmin(
      `${basePath}/api/v7/admin/queue-efficiency-per-day/adpass/${user.userId}`
    )
      .then((apiResult) => apiResult.json())
      .then((result) => {
        if (result && result.ok) {
          dispatch({
            type: types.SET_QUEUE_EFFICIENCY_PER_DAY_DATA,
            queueEfficiencyPerDayData: result.queueEfficiencyPerDayData,
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching queue efficiency per day data:", err);
      });
  };
}

export function getAdminQueueOutcomeDistributionData({ user }) {
  return function (dispatch) {
    if (!user || !user.userId) return;

    fetchAdmin(
      `${basePath}/api/v7/admin/queue-outcome-distribution/adpass/${user.userId}`
    )
      .then((apiResult) => apiResult.json())
      .then((result) => {
        if (result && result.ok) {
          dispatch({
            type: types.SET_QUEUE_OUTCOME_DISTRIBUTION_DATA,
            queueOutcomeDistributionData: result.queueOutcomeDistributionData,
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching queue outcome distribution data:", err);
      });
  };
}

export function getAdminMatchmakingCapacityTrendData({ user, days = 7 }) {
  return function (dispatch) {
    if (!user || !user.userId || !days) return;

    fetchAdmin(
      `${basePath}/api/v7/admin/matchmaking-capacity-trend/${days}/adpass/${user.userId}`
    )
      .then((apiResult) => apiResult.json())
      .then((result) => {
        if (result && result.ok) {
          dispatch({
            type: types.SET_MATCHMAKING_CAPACITY_TREND_DATA,
            matchmakingCapacityTrendData: result.matchmakingCapacityTrendData || [],
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching matchmaking capacity trend data:", err);
      });
  };
}

export function getAdminServersAliveCount({ user }) {
  return function (dispatch) {
    if (!user || !user.userId) return;

    fetchAdmin(
      `${basePath}/api/v7/admin/servers-alive-count/adpass/${user.userId}`
    )
      .then((apiResult) => apiResult.json())
      .then((result) => {
        if (result && result.ok) {
          dispatch({
            type: types.SET_SERVERS_ALIVE_COUNT,
            serversAliveCount: result.serversAliveCount,
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching servers alive count:", err);
      });
  };
}